import mapImage from "@/assets/india-melbourne-map.jpg";
import airplaneIcon from "@/assets/airplane-icon.png";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import { useParallax } from "@/hooks/use-parallax";

interface JourneyStepProps {
  number: number;
  color: string;
  title: string;
  description: {
    full: string;
    mobile: string;
  };
  index: number;
}

const JourneyStep = ({ number, color, title, description, index }: JourneyStepProps) => {
  const isMobile = useIsMobile();
  const [stepRef, stepVisible] = useIntersectionObserver({ 
    rootMargin: "50px",
    threshold: 0.3 
  });

  const colorClasses = {
    primary: "bg-primary/10 text-primary",
    secondary: "bg-secondary/10 text-secondary",
    accent: "bg-accent/10 text-accent"
  };

  return (
    <div 
      ref={stepRef}
      className={`flex gap-3 md:gap-4 transition-all duration-700 ${
        stepVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full ${colorClasses[color as keyof typeof colorClasses]} flex items-center justify-center flex-shrink-0`}>
        <span className="text-lg md:text-xl font-bold">{number}</span>
      </div>
      <div>
        <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">
          {title}
        </h3>
        <p className="text-sm md:text-base text-muted-foreground">
          <span className="hidden md:inline">
            {description.full}
          </span>
          <span className="md:hidden">
            {description.mobile}
          </span>
        </p>
      </div>
    </div>
  );
};

const HomeToHorizon = () => {
  const isMobile = useIsMobile();
  const [contentRef, contentVisible] = useIntersectionObserver();
  const airplaneParallax = useParallax({ speed: 0.15, direction: "down", disabled: isMobile });
  
  const journeySteps = [
    {
      number: 1,
      color: "primary",
      title: "Pre-Departure Support",
      description: {
        full: "We coordinate with families for safe travel assistance and guidance before you depart.",
        mobile: "Family coordination before departure."
      }
    },
    {
      number: 2,
      color: "secondary",
      title: "In-Flight Peace of Mind",
      description: {
        full: "Real-time flight tracking and updates sent to families throughout the journey.",
        mobile: "Real-time flight tracking for families."
      }
    },
    {
      number: 3,
      color: "accent",
      title: "Warm Melbourne Welcome",
      description: {
        full: "Personal pickup at Melbourne airport and immediate settling assistance begins.",
        mobile: "Airport pickup and settling begins."
      }
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left side - Image - Hidden on mobile */}
            {!isMobile && (
              <div className="relative h-[400px] lg:h-[500px]">
                <div className="absolute inset-0 bg-gradient-to-br from-secondary/20 to-primary/20 rounded-3xl blur-2xl" />
                <img
                  src={mapImage}
                  alt="Journey from India to Melbourne Australia"
                  className="relative rounded-3xl shadow-card w-full h-full object-cover"
                />
                <div 
                  ref={airplaneParallax.ref}
                  style={airplaneParallax.style}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                >
                  <div className="w-20 h-20 rounded-full bg-accent/90 flex items-center justify-center shadow-xl animate-pulse backdrop-blur-sm">
                    <img src={airplaneIcon} alt="Airplane" className="w-12 h-12 object-contain" />
                  </div>
                </div>
              </div>
            )}

            {/* Right side - Content */}
            <div 
              ref={contentRef}
              className={`space-y-4 md:space-y-6 transition-all duration-700 ${
                contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
                <span className="hidden md:inline">From Your Doorstep to Your{" "}</span>
                <span className="md:hidden">Doorstep to{" "}</span>
                <span className="text-secondary">Home in Australia.</span>
              </h2>
              
              <p className="text-base md:text-xl text-muted-foreground leading-relaxed">
                <span className="hidden md:inline">
                  Our unique 'Home to Horizon' service ensures your journey is cared for 
                  from the very start - coordination from your home country and a warm welcome in Melbourne.
                </span>
                <span className="md:hidden">
                  Coordinated support from India to Melbourne arrival.
                </span>
              </p>

              <div className="space-y-3 md:space-y-4 pt-2 md:pt-4">
                {journeySteps.map((step, index) => (
                  <JourneyStep key={step.number} {...step} index={index} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeToHorizon;
